<script lang="ts">
import { defineComponent } from 'vue';
import { useSportsStore } from '@/stores/sports';
import { mapActions, mapState } from 'pinia';
import { useTranslationsStore } from '@/stores/translations';
import Headline from '@/components/headline.vue';

export default defineComponent({
  name: 'sports',
  components: { Headline },
  computed: {
    ...mapState(useSportsStore, ['records', 'loading']),
    ...mapState(useTranslationsStore, ['languages']),
    priorityLanguages() {
      return [
        ...this.languages,
        { value: 'default', name: 'По-умолчанию' },
      ] as typeof this.languages;
    },
    fromNotEmpty() {
      if (this.fromRecords) {
        // @ts-expect-error describe types
        return this.fromRecords.some((record) => record.Priority.length);
      }
      return false;
    },
  },
  data() {
    return {
      lang: 'en',
      from: 'en',
      fromRecords: null as null | [],
    };
  },
  methods: {
    ...mapActions(useSportsStore, [
      'fetchRecords',
      'loadRecords',
      'updatePriority',
      'copyPriorities',
    ]),
    changeLanguage() {
      this.from = this.lang;
      this.fromRecords = null;
      this.fetchRecords(this.lang);
    },
    changeLanguageFrom() {
      if (this.from === this.lang) {
        this.fromRecords = null;
        return;
      }

      this.loadRecords(this.from).then((from) => (this.fromRecords = from));
    },
    copy(ev: any) {
      if (this.from === this.lang) {
        return;
      }

      this.copyPriorities(this.from, this.lang).then(() => {
        this.fetchRecords(this.lang);
      });
    },
    changed(ev: any) {
      ev.preventDefault();
      const oldValue = ev.target.getAttribute('data-value');
      const newValue = ev.target?.value?.length ? ev.target.value : null;
      const id = ev.target.getAttribute('id');

      if (oldValue === newValue) return;

      ev.target.setAttribute('data-value', newValue);

      this.updatePriority(this.lang, id, newValue ? parseInt(newValue) : null);
    },
  },
  mounted() {
    if (!this.records) {
      this.changeLanguage();
    }
  },
});
</script>

<template>
  <div class="grid gap-2">
    <template v-if="loading">
      <div class="custom-background" style="height: 90vh">Загрузка...</div>
    </template>
    <template v-else-if="!records">
      <div class="custom-background" style="height: 90vh">Нет записей или доступ ограничен</div>
    </template>
    <template v-else>
      <teleport to="#title">
        <headline class="mx-4 my-2">Приоритет отображения видов спорта</headline>
      </teleport>
      <table class="basic-table mx-3" style="max-width: 950px">
        <thead>
          <tr>
            <th style="width: 80px" class="!text-left">ID</th>
            <th style="width: 200px">Спорт</th>
            <th>
              <span> Текущий приоритет </span>
              <select v-model="lang" @change="changeLanguage" class="ml-2 p-2">
                <option
                  v-for="language in priorityLanguages"
                  :key="language.value"
                  :value="language.value"
                >
                  {{ language.name }}
                </option>
              </select>
            </th>
            <th class="flex place-items-center">
              <button
                v-if="from !== lang && fromNotEmpty"
                class="rounded cursor-pointer text-green-500"
                @click.prevent="copy"
                title="Скопировать референсные значения в текущие приоритеты"
              >
                <i class="pi pi-arrow-circle-left p-1 ml-1" style="font-size: 1.9rem" />
              </button>
              <span>Референс</span>
              <select v-model="from" @change="changeLanguageFrom" class="mx-2 p-2">
                <option v-for="language in priorityLanguages" :key="language.value" :value="language.value">
                  {{ language.name }}
                </option>
              </select>
            </th>
          </tr>
        </thead>
        <template v-for="sport in records" :key="sport.Id">
          <tr>
            <td class="!text-left">{{ sport.Id }}</td>
            <td class="text-center">{{ sport.Name }}</td>
            <td class="text-center">
              <input
                :id="sport.Id"
                :data-value="sport.Priority[0]?.order"
                :value="sport.Priority[0]?.order"
                @blur="changed"
                @keydown.enter="changed"
                class="text-center rounded-lg border border-transparent focus:border-gray-600 cursor-text"
              />
            </td>
            <td v-if="fromRecords" class="text-gray-500 text-center">
              {{ fromRecords.find((r) => r.Id === sport.Id)?.Priority[0]?.order }}
            </td>
            <td v-else class="text-gray-500 text-center" />
          </tr>
        </template>
      </table>
    </template>
  </div>
</template>
